import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Input, Form, Drawer, Button, message, Popconfirm, DatePicker, InputNumber, Select, Spin, Alert, Row, Col, Switch, Badge } from 'antd';
import { PlusOutlined, EditOutlined, CalendarOutlined, UnorderedListOutlined, BellOutlined, SoundOutlined, PrinterOutlined } from '@ant-design/icons';
import { ApiHelpers } from '../../../helpers';
import moment from 'moment';
import { momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useBookingValidation } from './RestaurantBookingsValidation';
import { useBookingsByRestaurant } from '../../../swr';
import { useBookingNotifications } from '../../../hooks/useBookingNotifications';
import BrowserFullscreenCalendar from './Bookings/BrowserFullscreenCalendar';
import { useLocation, useNavigate } from 'react-router-dom';
import CalendarLegend from './Bookings/CalendarLegend';

const { Column } = Table;
const { Search } = Input;
const { TextArea } = Input;
const { Option } = Select;

// Initialize localizer for the calendar
const localizer = momentLocalizer(moment);

const RestaurantBookings = (props) => {
    const [form] = Form.useForm();
    const [bookingsFilter, setBookingsFilter] = useState([]);
    const [newElement, setNewElement] = useState({});
    const [actionType, setActionType] = useState(props.actionType);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'list', or 'print'
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [drawerManuallyClosed, setDrawerManuallyClosed] = useState(false);
    const isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (typeof window.orientation !== "undefined") ||
            (navigator.userAgent.indexOf('IEMobile') !== -1);
    };
    // Print view state - PROPERLY DECLARED HERE
    const [printDate, setPrintDate] = useState(moment());
    const [printView, setPrintView] = useState('day'); // 'day', 'week', 'month'

    const location = useLocation();
    const navigate = useNavigate();

    // Destructure props to prevent dependency issues
    const { restaurantId, bookingId, shortUrl } = props;

    // New state variables for confirmation handling
    const [confirmationStatus, setConfirmationStatus] = useState(null);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmationLoading, setConfirmationLoading] = useState(false);

    // Memoize initial state to prevent recreation
    const INITIAL_STATE_ELEMENT = useMemo(() => ({
        uniqueIdentificationToken: null,
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        partySize: 2,
        reservationDate: '',
        hourStart: '',
        hourEnd: '',
        specialRequests: '',
        status: 'confirmed',
        sysReservationTypeName: '',
        restaurantToken: shortUrl
    }), [shortUrl]);

    // SWR Hook for bookings - Memoize config to prevent infinite re-renders
    const swrConfig = useMemo(() => ({
        refreshInterval: notificationsEnabled ? 30000 : 0 // Only refresh if notifications are enabled
    }), [notificationsEnabled]);

    const {
        bookings,
        rawData,
        isLoading,
        isError,
        mutate,
        refresh,
        hasValidToken
    } = useBookingsByRestaurant(shortUrl, swrConfig);

    // Memoize notification dependencies to prevent re-initialization
    const notificationDeps = useMemo(() =>
        hasValidToken ? bookings : [],
        [hasValidToken, bookings]
    );

    const notificationConfig = useMemo(() => ({
        enableNotifications: notificationsEnabled && hasValidToken,
        enableSound: soundEnabled && hasValidToken,
        restaurantId: restaurantId // Add restaurantId for URL routing
    }), [notificationsEnabled, hasValidToken, soundEnabled, restaurantId]);

    // Notifications Hook
    useBookingNotifications(notificationDeps, notificationConfig);

    // Import booking validation hooks with enhanced capacity functionality
    const {
        validateBooking,
        bookingSettings,
        fetchCapacity,
        getMaxPartySize,
        availableCapacity,
        capacityLoading,
        loading: loadingSettings
    } = useBookingValidation(shortUrl);

    // Handle input changes - MEMOIZED TO PREVENT INFINITE LOOPS
    const onChange = useCallback((event) => {
        const { name, value } = event.target;
        setNewElement(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    // Handle select changes - MEMOIZED TO PREVENT INFINITE LOOPS
    const onSelectChange = useCallback((value, field) => {
        setNewElement(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Handle number input changes - MEMOIZED TO PREVENT INFINITE LOOPS
    const onNumberChange = useCallback((value, field) => {
        setNewElement(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Handle drawer close - MEMOIZED TO PREVENT INFINITE LOOPS - CLEAR URL PARAMS
    const onClose = useCallback(() => {
        setDrawerManuallyClosed(true);
        setDrawerVisible(false);

        // Clear URL parameters when closing drawer
        navigate(`/admin/locations/${restaurantId}/bookings`, { replace: true });

        // Reset form
        form.resetFields();
        setNewElement({});
        setSelectedBooking(null);
        setAvailableTimeSlots([]);
    }, [form, restaurantId, navigate]);

    // Reset form - MEMOIZED TO PREVENT INFINITE LOOPS
    const onReset = useCallback(() => {
        form.resetFields();
        setNewElement({});
        setSelectedBooking(null);
        setAvailableTimeSlots([]);
    }, [form]);

    // Handle date change - MEMOIZED TO PREVENT INFINITE LOOPS
    const onDateChange = useCallback((date) => {
        const dateStr = date ? date.format('YYYY-MM-DD') : '';

        setNewElement(prev => ({
            ...prev,
            reservationDate: dateStr,
            hourStart: '',
            hourEnd: ''
        }));

        // Clear time fields in form
        form.setFieldsValue({
            hourStart: undefined,
            hourEnd: undefined
        });

        // Fetch capacity data for the selected date
        if (dateStr && fetchCapacity) {
            fetchCapacity(dateStr);
        }
    }, [form, fetchCapacity]);

    // Add new booking - MEMOIZED TO PREVENT INFINITE LOOPS
    const onAddElement = useCallback(() => {
        setDrawerManuallyClosed(false);
        setActionType('new');

        ApiHelpers.getUUID()
            .then((response) => {
                const today = moment().format('YYYY-MM-DD');
                setNewElement({
                    ...INITIAL_STATE_ELEMENT,
                    uniqueIdentificationToken: response.data.uuid,
                    reservationDate: today
                });

                form.setFieldsValue({
                    status: 'confirmed',
                    reservationDate: moment(today),
                    partySize: 2
                });

                // Fetch capacity data for today
                if (fetchCapacity) {
                    fetchCapacity(today);
                }

                setDrawerVisible(true);
            }, (error) => {
                console.log(error);
            });
    }, [INITIAL_STATE_ELEMENT, form, fetchCapacity]);

    // Edit booking - MEMOIZED TO PREVENT INFINITE LOOPS - ADD URL ROUTING (SINGLE DECLARATION)
    const onEditElement = useCallback((record) => {
        setDrawerManuallyClosed(false);
        setActionType('edit');
        setSelectedBooking(record);

        // Update URL with booking token
        const newUrl = `/admin/locations/${restaurantId}/bookings?booking=${record.uniqueIdentificationToken}`;
        navigate(newUrl, { replace: true });

        const reservationDateStr = record.reservationDate ?
            (record.reservationDate.includes('T') ?
                record.reservationDate.split('T')[0] :
                record.reservationDate) : '';
        const dateValue = reservationDateStr ? moment(reservationDateStr) : null;

        const hourStartStr = record.hourStart ?
            (record.hourStart.includes(':') ?
                record.hourStart.split(':').slice(0, 2).join(':') :
                record.hourStart) : '';
        const hourEndStr = record.hourEnd ?
            (record.hourEnd.includes(':') ?
                record.hourEnd.split(':').slice(0, 2).join(':') :
                record.hourEnd) : '';

        const isPastReservation = moment(reservationDateStr).isBefore(moment().startOf('day'));

        setNewElement({
            ...record,
            reservationDate: reservationDateStr,
            hourStart: hourStartStr,
            hourEnd: hourEndStr,
            restaurantToken: shortUrl,
            isPastReservation,
        });

        if (reservationDateStr && fetchCapacity) {
            fetchCapacity(reservationDateStr);
        }

        form.setFieldsValue({
            ...record,
            reservationDate: dateValue,
            isPastReservation
        });

        setDrawerVisible(true);
    }, [shortUrl, fetchCapacity, form, restaurantId, navigate]);

    // Check if this is a confirmation URL or booking URL - MEMOIZE TO PREVENT INFINITE LOOPS
    const handleUrlParams = useCallback(async () => {
        // Parse the URL to check for parameters
        const searchParams = new URLSearchParams(location.search);
        const confirmation = searchParams.get('confirmation');
        const bookingToken = searchParams.get('booking');

        // Handle confirmation URL
        if (shortUrl && confirmation !== null && !confirmationLoading) {
            setConfirmationLoading(true);
            try {
                // Call API to update booking status
                const response = await ApiHelpers.updateBookingStatus(
                    shortUrl,
                    bookingId,
                    confirmation
                );

                if (response.data && response.data.statusCode === 200) {
                    // Set success status and message
                    setConfirmationStatus('success');
                    setConfirmationMessage(
                        confirmation === 'true'
                            ? 'Rezervarea a fost confirmată cu succes!'
                            : 'Rezervarea a fost anulată.'
                    );

                    // Refresh the bookings using SWR mutate
                    mutate();

                    // Clear URL parameters but keep the current location
                    navigate(
                        `/admin/locations/${restaurantId}/bookings`,
                        { replace: true }
                    );
                } else {
                    setConfirmationStatus('error');
                    setConfirmationMessage('Nu am putut actualiza status-ul. Va rugăm sa încercați din nou.');
                }
            } catch (error) {
                console.error('Error updating booking status:', error);
                setConfirmationStatus('error');
                setConfirmationMessage('A intervenit o eroare.');
            } finally {
                setConfirmationLoading(false);
            }
        }

        // Handle booking URL - open modal for specific booking
        if (bookingToken && bookings.length > 0 && !drawerVisible && !drawerManuallyClosed) {
            const booking = bookings.find(b => b.uniqueIdentificationToken === bookingToken);
            if (booking) {
                onEditElement(booking);
            } else {
                // Booking not found, clear invalid URL parameter
                navigate(`/admin/locations/${restaurantId}/bookings`, { replace: true });
                message.warning('Rezervarea nu a fost găsită sau nu mai este disponibilă.');
            }
        }
    }, [shortUrl, location.search, navigate, restaurantId, mutate, bookingId, confirmationLoading, bookings, drawerVisible, drawerManuallyClosed, onEditElement]);

    // Update filtered bookings when bookings change - USE CALLBACK TO PREVENT INFINITE LOOPS
    const updateFilteredBookings = useCallback(() => {
        setBookingsFilter(prevFilter => {
            // Only update if bookings actually changed
            if (JSON.stringify(prevFilter) !== JSON.stringify(bookings)) {
                return bookings;
            }
            return prevFilter;
        });
    }, [bookings]);

    useEffect(() => {
        updateFilteredBookings();
    }, [updateFilteredBookings]);

    useEffect(() => {
        handleUrlParams();
    }, [handleUrlParams]);

    // Update available time slots when capacity data changes - OPTIMIZE TO PREVENT INFINITE LOOPS
    const updateAvailableTimeSlots = useCallback(() => {
        if (availableCapacity && availableCapacity.length > 0) {
            // Format and filter time slots
            const slots = availableCapacity.map(slot => ({
                time: slot.time ? slot.time.substring(0, 5) : '', // Format to HH:MM
                capacity: slot.capacity,
                disabled: slot.capacity <= 0
            }));

            // Add the currently selected times to the list if they're not already there
            if (newElement.hourStart && !slots.some(slot => slot.time === newElement.hourStart)) {
                slots.push({
                    time: newElement.hourStart,
                    capacity: newElement.partySize || 2,
                    disabled: false
                });
            }

            if (newElement.hourEnd && !slots.some(slot => slot.time === newElement.hourEnd)) {
                slots.push({
                    time: newElement.hourEnd,
                    capacity: newElement.partySize || 2,
                    disabled: false
                });
            }

            // Sort the slots by time
            slots.sort((a, b) => a.time.localeCompare(b.time));

            // Filter out past time slots for today
            const selectedDate = moment(newElement.reservationDate);
            const today = moment().startOf('day');

            if (selectedDate.isValid() && selectedDate.isSame(today, 'day')) {
                const minAdvanceHours = bookingSettings?.minAdvanceHours || 0;
                const minFutureTime = moment().add(minAdvanceHours, 'hours');

                const filteredSlots = slots.filter(slot => {
                    const slotTime = moment(slot.time, 'HH:mm');
                    const slotDateTime = moment(today).hour(slotTime.hour()).minute(slotTime.minute());
                    return slotDateTime.isAfter(minFutureTime);
                });

                setAvailableTimeSlots(filteredSlots);
            } else {
                setAvailableTimeSlots(slots);
            }
        }
    }, [
        availableCapacity,
        newElement.reservationDate,
        newElement.hourStart,
        newElement.hourEnd,
        newElement.partySize,
        bookingSettings?.minAdvanceHours
    ]);

    useEffect(() => {
        updateAvailableTimeSlots();
    }, [updateAvailableTimeSlots]);

    // Add this effect to expose a global function
    useEffect(() => {
        // Expose function globally for notifications
        window.openBookingFromNotification = (bookingId) => {
            if (bookings.length > 0) {
                const booking = bookings.find(b => b.uniqueIdentificationToken === bookingId);
                if (booking) {
                    // Update URL first
                    navigate(`/admin/locations/${restaurantId}/bookings?booking=${bookingId}`);
                    // Then open modal
                    setTimeout(() => {
                        onEditElement(booking);
                    }, 50);
                }
            }
        };

        return () => {
            delete window.openBookingFromNotification;
        };
    }, [bookings, onEditElement, navigate, restaurantId]);

    // Add this useEffect to handle page visibility issues
    useEffect(() => {
        let visibilityTimeout;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is being hidden - clear any pending operations
                clearTimeout(visibilityTimeout);
            } else {
                // Page is visible again - restore functionality
                visibilityTimeout = setTimeout(() => {
                    // Force a small re-render to ensure page is responsive
                    setViewMode(prev => prev);

                    // Check URL params in case we missed a notification click
                    const searchParams = new URLSearchParams(location.search);
                    const bookingToken = searchParams.get('booking');
                    if (bookingToken && bookings.length > 0 && !drawerVisible) {
                        const booking = bookings.find(b => b.uniqueIdentificationToken === bookingToken);
                        if (booking) {
                            onEditElement(booking);
                        }
                    }
                }, 100);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);
        window.addEventListener('pageshow', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
            window.removeEventListener('pageshow', handleVisibilityChange);
            clearTimeout(visibilityTimeout);
        };
    }, [location.search, bookings, drawerVisible, onEditElement, setViewMode]);

    // Handle time changes - HEAVILY OPTIMIZED TO PREVENT INFINITE LOOPS
    const onTimeChange = useCallback((time, timeField) => {
        const timeStr = time ? time.format('HH:mm') : '';

        if (timeField === 'hourStart') {
            const currentPartySize = form.getFieldValue('partySize') || newElement.partySize || 2;
            const selectedSlot = availableTimeSlots.find(slot => slot.time === timeStr);

            if (selectedSlot && currentPartySize > selectedSlot.capacity) {
                form.setFieldsValue({
                    partySize: selectedSlot.capacity
                });

                setNewElement(prev => ({
                    ...prev,
                    hourStart: timeStr,
                    partySize: selectedSlot.capacity
                }));

                message.info(`Party size adjusted to maximum capacity (${selectedSlot.capacity}) for this time slot.`);
            } else {
                setNewElement(prev => ({
                    ...prev,
                    hourStart: timeStr
                }));
            }

            // Calculate end time based on slot duration from booking settings
            if (time && bookingSettings?.days && newElement.reservationDate) {
                const dayIndex = moment(newElement.reservationDate).day();
                let dayProperty = '';
                switch (dayIndex) {
                    case 0: dayProperty = 'sunday'; break;
                    case 1: dayProperty = 'monday'; break;
                    case 2: dayProperty = 'tuesday'; break;
                    case 3: dayProperty = 'wednesday'; break;
                    case 4: dayProperty = 'thursday'; break;
                    case 5: dayProperty = 'friday'; break;
                    case 6: dayProperty = 'saturday'; break;
                    default: dayProperty = '';
                }

                const daySettings = bookingSettings.days[dayProperty];
                if (daySettings?.enabled) {
                    const startTimeMoment = moment(timeStr, 'HH:mm');
                    const applicableSlot = daySettings.timeSlots?.find(slot => {
                        const slotStartTimeString = slot.startTime || '00:00:00';
                        const slotEndTimeString = slot.endTime || '00:00:00';
                        const cleanStartTime = slotStartTimeString.split('.')[0];
                        const cleanEndTime = slotEndTimeString.split('.')[0];
                        const slotStartTime = moment(cleanStartTime, 'HH:mm:ss');
                        const slotEndTime = moment(cleanEndTime, 'HH:mm:ss');

                        return startTimeMoment.isSameOrAfter(slotStartTime) && startTimeMoment.isBefore(slotEndTime);
                    });

                    if (applicableSlot) {
                        const endTime = moment(timeStr, 'HH:mm').add(applicableSlot.timeSlotDuration, 'minutes');
                        const slotEndTimeString = applicableSlot.endTime || '00:00:00';
                        const cleanEndTime = slotEndTimeString.split('.')[0];
                        const slotEndTime = moment(cleanEndTime, 'HH:mm:ss');
                        const finalEndTime = endTime.isAfter(slotEndTime) ? slotEndTime : endTime;

                        form.setFieldsValue({
                            hourEnd: finalEndTime
                        });

                        setNewElement(prev => ({
                            ...prev,
                            hourStart: timeStr,
                            hourEnd: finalEndTime.format('HH:mm')
                        }));
                    }
                }
            }
        } else if (timeField === 'hourEnd') {
            setNewElement(prev => ({
                ...prev,
                hourEnd: timeStr
            }));
        }
    }, [form, newElement.partySize, newElement.reservationDate, availableTimeSlots, bookingSettings]);

    // Handle calendar event selection - MEMOIZED TO PREVENT INFINITE LOOPS
    const handleSelectEvent = useCallback((event) => {
        onEditElement(event);
    }, [onEditElement]);

    // Handle search - MEMOIZED TO PREVENT INFINITE LOOPS
    const handleSearch = useCallback((event) => {
        const value = event.target.value.toLowerCase();
        if (value) {
            setBookingsFilter(bookings.filter(item => {
                return (
                    item.customerName?.toLowerCase().includes(value) ||
                    item.customerPhone?.toLowerCase().includes(value) ||
                    (item.customerEmail && item.customerEmail.toLowerCase().includes(value)) ||
                    (item.tableNumber && item.tableNumber.toString().includes(value)) ||
                    (item.specialRequests && item.specialRequests.toLowerCase().includes(value))
                );
            }));
        } else {
            setBookingsFilter(bookings);
        }
    }, [bookings]);

    // Delete booking - MEMOIZED TO PREVENT INFINITE LOOPS
    const onDelete = useCallback(() => {
        if (!newElement.uniqueIdentificationToken || !newElement.restaurantToken) {
            message.error('Invalid booking data');
            return;
        }

        ApiHelpers.deleteBooking(newElement.uniqueIdentificationToken, newElement.restaurantToken)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    message.success('Rezervarea a fost ștearsă!');
                    // Refresh bookings using SWR mutate
                    mutate();
                }
            }, (error) => {
                console.log(error);
                message.error('Eroare la ștergere!')
            });
        onClose();
    }, [newElement.uniqueIdentificationToken, newElement.restaurantToken, mutate, onClose]);

    // Save booking - MEMOIZED TO PREVENT INFINITE LOOPS
    const onSave = useCallback(() => {
        // Validate form
        form.validateFields()
            .then(() => {
                // Prepare booking data for API
                const bookingData = { ...newElement };

                // Format date and times for API submission
                if (bookingData.reservationDate && !bookingData.reservationDate.includes('T')) {
                    bookingData.reservationDate = `${bookingData.reservationDate}T00:00:00`;
                }

                // Add seconds to time if not present
                if (bookingData.hourStart && !bookingData.hourStart.includes(':00', bookingData.hourStart.length - 3)) {
                    bookingData.hourStart = `${bookingData.hourStart}:00`;
                }

                if (bookingData.hourEnd && !bookingData.hourEnd.includes(':00', bookingData.hourEnd.length - 3)) {
                    bookingData.hourEnd = `${bookingData.hourEnd}:00`;
                }

                // Validate booking against restaurant booking settings
                if (validateBooking && !validateBooking(bookingData)) {
                    return;
                }

                if (actionType === 'new') {
                    ApiHelpers.postBooking(bookingData)
                        .then((response) => {
                            //message.success('Rezervare adăugată!');
                            // Refresh bookings using SWR mutate
                            mutate();

                            // Close drawer and clear URL
                            onClose();

                            // Update capacity data after adding a reservation
                            const dateStr = bookingData.reservationDate.split('T')[0];
                            if (dateStr && fetchCapacity) {
                                fetchCapacity(dateStr);
                            }
                        }, (error) => {
                            console.log(error);
                            message.error('Eroare la salvare!')
                        });
                } else if (actionType === 'edit') {
                    ApiHelpers.updateBooking(bookingData)
                        .then((response) => {
                            //message.success('Rezervare modificată!');
                            // Refresh bookings using SWR mutate
                            mutate();

                            // Close drawer and clear URL
                            onClose();

                            // Update capacity data after updating a reservation
                            const dateStr = bookingData.reservationDate.split('T')[0];
                            if (dateStr && fetchCapacity) {
                                fetchCapacity(dateStr);
                            }
                        }, (error) => {
                            console.log(error);
                            message.error('Eroare la actualizare!');
                        });
                }
            })
            .catch((info) => {

            });
    }, [form, newElement, validateBooking, actionType, mutate, onClose, fetchCapacity]);

    // Handle quick status change - MEMOIZED TO PREVENT INFINITE LOOPS
    const handleQuickStatusChange = useCallback((newStatus) => {
        // Update the form field
        form.setFieldsValue({
            status: newStatus
        });

        // Update the state
        setNewElement(prev => ({
            ...prev,
            status: newStatus
        }));

        // Automatically trigger save
        setTimeout(() => {
            // Validate form first
            form.validateFields()
                .then(() => {
                    // Prepare booking data for API
                    const bookingData = { ...newElement, status: newStatus };

                    // Format date and times for API submission
                    if (bookingData.reservationDate && !bookingData.reservationDate.includes('T')) {
                        bookingData.reservationDate = `${bookingData.reservationDate}T00:00:00`;
                    }

                    // Add seconds to time if not present
                    if (bookingData.hourStart && !bookingData.hourStart.includes(':00', bookingData.hourStart.length - 3)) {
                        bookingData.hourStart = `${bookingData.hourStart}:00`;
                    }

                    if (bookingData.hourEnd && !bookingData.hourEnd.includes(':00', bookingData.hourEnd.length - 3)) {
                        bookingData.hourEnd = `${bookingData.hourEnd}:00`;
                    }

                    // Validate booking against restaurant booking settings
                    if (validateBooking && !validateBooking(bookingData)) {
                        return;
                    }

                    // Update booking with new status
                    if (actionType === 'edit') {
                        ApiHelpers.updateBooking(bookingData)
                            .then((response) => {
                                const statusMessages = {
                                    'confirmed': 'Rezervare confirmată!',
                                    'pending': 'Rezervare setată în așteptare!',
                                    'cancelled': 'Rezervare anulată!'
                                };
                                //message.success(statusMessages[newStatus] || 'Rezervare actualizată!');

                                // Refresh bookings using SWR mutate
                                mutate();

                                // Close drawer and clear URL - this ensures consistent behavior
                                onClose();

                                // Update capacity data after updating a reservation
                                const dateStr = bookingData.reservationDate.split('T')[0];
                                if (dateStr && fetchCapacity) {
                                    fetchCapacity(dateStr);
                                }
                            }, (error) => {
                                console.log(error);
                                message.error('Eroare la actualizarea statusului!')
                            });
                    } else {
                        message.warning('Salvați mai întâi rezervarea pentru a schimba statusul.');
                    }
                })
                .catch((info) => {

                    message.error('Completați toate câmpurile obligatorii înainte de a schimba statusul.');
                });
        }, 100); // Small delay to ensure state update
    }, [form, newElement, validateBooking, actionType, mutate, onClose, fetchCapacity]);

    // Filter bookings for print view - PROPERLY SCOPED
    const getFilteredBookingsForPrint = useCallback(() => {
        let startDate, endDate;

        if (printView === 'day') {
            startDate = moment(printDate).startOf('day');
            endDate = moment(printDate).endOf('day');
        } else if (printView === 'week') {
            startDate = moment(printDate).startOf('week');
            endDate = moment(printDate).endOf('week');
        } else { // month
            startDate = moment(printDate).startOf('month');
            endDate = moment(printDate).endOf('month');
        }

        return bookingsFilter.filter(booking => {
            const bookingDate = moment(booking.reservationDate?.split('T')[0]);
            return bookingDate.isBetween(startDate, endDate, 'day', '[]');
        }).sort((a, b) => {
            // Sort by date, then by time
            const dateA = moment(a.reservationDate?.split('T')[0] + ' ' + a.hourStart);
            const dateB = moment(b.reservationDate?.split('T')[0] + ' ' + b.hourStart);
            return dateA.diff(dateB);
        });
    }, [bookingsFilter, printDate, printView]);

    // Print view navigation - PROPERLY SCOPED
    const PrintViewNavigation = useCallback(() => (
        <div className="print-navigation rbc-toolbar" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="rbc-btn-group">
                <Button onClick={() => setPrintDate(moment())}>
                    Astăzi
                </Button>
                <Button onClick={() => setPrintDate(moment(printDate).subtract(1, printView))}>
                    Înapoi
                </Button>
                <Button onClick={() => setPrintDate(moment(printDate).add(1, printView))}>
                    Înainte
                </Button>
            </div>

            <div className="print-date-title">
                {printView === 'day' && printDate.format('DD MMMM YYYY')}
                {printView === 'week' && `Săptămâna ${printDate.format('DD MMMM')} - ${moment(printDate).endOf('week').format('DD MMMM YYYY')}`}
                {printView === 'month' && printDate.format('MMMM YYYY')}
            </div>

            <div className="rbc-btn-group">
                <Button
                    type={printView === 'day' ? 'primary' : 'default'}
                    onClick={() => setPrintView('day')}
                >
                    Zi
                </Button>
                <Button
                    type={printView === 'week' ? 'primary' : 'default'}
                    onClick={() => setPrintView('week')}
                >
                    Săptămână
                </Button>
                <Button
                    type={printView === 'month' ? 'primary' : 'default'}
                    onClick={() => setPrintView('month')}
                >
                    Lună
                </Button>
            </div>
        </div>
    ), [printDate, printView, setPrintDate, setPrintView]);

    // Memoize components to prevent unnecessary re-renders
    const CustomEvent = useCallback(({ event }) => {
        return (
            <div className="custom-event">
                <div className="event-customer">{event.customerName} - <span>{event.partySize}p</span></div>
                <div className="event-details">
                    <span>{event.formattedHourStart} • {event.formattedHourEnd}</span>
                </div>
            </div>
        );
    }, []);

    // Custom event style for calendar - MEMOIZED TO PREVENT INFINITE LOOPS - FIXED THE DUPLICATE DECLARATION
    const eventStyleGetter = useCallback((event) => {
        let style = {
            backgroundColor: '#3174ad',
            borderRadius: '5px',
            color: 'white',
            border: '1px solid',
            display: 'block'
        };

        if (event.status === 'confirmed') {
            style.backgroundColor = '#28a745';
            style.borderColor = '#1e7e34';
        } else if (event.status === 'pending') {
            style.backgroundColor = '#ffc107';
            style.borderColor = '#d39e00';
            style.color = '#212529';
        } else if (event.status === 'cancelled') {
            if (event.cancelledByClient === true) {
                style.backgroundColor = '#f90f7e';
                style.borderColor = '#ad1457';
            } else {
                style.backgroundColor = '#dc3545';
                style.borderColor = '#bd2130';
            }
        }

        return { style };
    }, []);

    // Toggle between calendar and list view - MEMOIZED TO PREVENT INFINITE LOOPS
    const toggleViewMode = useCallback((mode) => {
        setViewMode(mode);
    }, []);

    // Validate that end time is after start time - MEMOIZED TO PREVENT INFINITE LOOPS
    const validateEndTime = useCallback((_, value) => {
        const startTime = form.getFieldValue('hourStart');

        if (value && startTime && typeof startTime.format === 'function' && typeof value.format === 'function') {
            const startMoment = moment(startTime.format('HH:mm'), 'HH:mm');
            const endMoment = moment(value.format('HH:mm'), 'HH:mm');

            if (!endMoment.isAfter(startMoment)) {
                return Promise.reject('End time must be after start time');
            }
        }

        return Promise.resolve();
    }, [form]);

    // Memoize calendar messages to prevent re-creation
    const calendarMessages = useMemo(() => ({
        month: 'Lună',
        week: 'Săptămână',
        day: 'Zi',
        today: 'Astăzi',
        previous: 'Înapoi',
        next: 'Înainte'
    }), []);

    // Memoize calendar components to prevent re-creation
    const calendarComponents = useMemo(() => ({
        event: CustomEvent
    }), [CustomEvent]);

    const getSortedBookings = useCallback(() => {
        const today = moment().startOf('day'); // Get today's date at 00:00:00

        return [...bookingsFilter]
            .filter(booking => {
                // Filter to only include bookings from today onwards
                const bookingDate = moment(booking.reservationDate?.split('T')[0] || booking.dateStart);
                return bookingDate.isSameOrAfter(today);
            })
            .sort((a, b) => {
                // Pending reservations first
                if (a.status === 'pending' && b.status !== 'pending') return -1;
                if (a.status !== 'pending' && b.status === 'pending') return 1;

                // Then sort by date and time
                const dateA = moment(a.reservationDate?.split('T')[0] + ' ' + a.hourStart);
                const dateB = moment(b.reservationDate?.split('T')[0] + ' ' + b.hourStart);
                return dateA.diff(dateB);
            });
    }, [bookingsFilter]);

    // Calculate pending bookings count
    const pendingCount = useMemo(() => {
        return getSortedBookings().filter(booking => booking.status === 'pending').length;
    }, [getSortedBookings]);

    // Show loading or error from SWR
    if (!hasValidToken) {
        return (
            <div className="error-container">
                <Alert
                    message="Configuration Error"
                    description="Restaurant token is invalid or missing. Please check your restaurant configuration."
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="error-container">
                <Alert
                    message="Error"
                    description="Nu s-au putut încărca rezervările. Vă rugăm să reîncercați."
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={refresh}>
                            Reîncercați
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <>
            {/* Display confirmation message if there is one */}
            {confirmationStatus && (
                <Alert
                    message={confirmationStatus === 'success' ? "Success" : "Error"}
                    description={confirmationMessage}
                    type={confirmationStatus}
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                    afterClose={() => setConfirmationStatus(null)}
                />
            )}

            {/* Show loading indicator during confirmation process */}
            {confirmationLoading && (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin size="large" />
                    <p>Processing booking status update...</p>
                </div>
            )}


            <div className="bookings-header">
                <Button type="primary" onClick={() => onAddElement()} className="btnAdd">
                    <PlusOutlined /> Adaugă rezervare
                </Button>
                <div className="view-toggle">
                    <Button.Group>
                        <Button
                            type={viewMode === 'calendar' ? 'primary' : 'default'}
                            icon={<CalendarOutlined />}
                            onClick={() => toggleViewMode('calendar')}
                            className={`pill-btn ${viewMode === 'calendar' ? 'pill-active' : ''}`}
                        >
                            Calendar
                        </Button>
                        <Button
                            type={viewMode === 'list' ? 'primary' : 'default'}
                            icon={<UnorderedListOutlined />}
                            onClick={() => toggleViewMode('list')}
                            className={`pill-btn ${viewMode === 'list' ? 'pill-active' : ''}`}
                        >
                            Listă
                            {pendingCount > 0 && (
                                <Badge
                                    count={pendingCount}

                                    dot={false}
                                    style={{ marginLeft: '8px' }}
                                />
                            )}
                        </Button>
                        <Button
                            type={viewMode === 'print' ? 'primary' : 'default'}
                            icon={<PrinterOutlined />}
                            onClick={() => toggleViewMode('print')}
                            className={`pill-btn ${viewMode === 'print' ? 'pill-active' : ''}`}
                        >
                            Print
                        </Button>
                    </Button.Group>
                </div>
                <Search
                    placeholder="Caută"
                    onChange={handleSearch}
                    className="tableSearchField"
                />
            </div>

            {isLoading ? (
                <div className="loading-container">
                    <Spin size="large" />
                </div>
            ) : viewMode === 'calendar' ? (
                <div>
                    <BrowserFullscreenCalendar
                        localizer={localizer}
                        events={bookingsFilter}
                        bookings={bookingsFilter}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 800 }}
                        onSelectEvent={handleSelectEvent}
                        eventPropGetter={eventStyleGetter}
                        views={['month', 'week', 'day']}
                        defaultView="week"
                        components={calendarComponents}
                        messages={calendarMessages}
                    />
                    {/* Notification Controls */}
                    {!isMobile() ?
                        <div className="notification-controls" style={{ marginBottom: 16, display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <BellOutlined />
                                <span>Notificări:</span>
                                <Switch
                                    checked={notificationsEnabled}
                                    onChange={setNotificationsEnabled}
                                    checkedChildren="ON"
                                    unCheckedChildren="OFF"
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <SoundOutlined />
                                <span>Sunet:</span>
                                <Switch
                                    checked={soundEnabled}
                                    onChange={setSoundEnabled}
                                    checkedChildren="ON"
                                    unCheckedChildren="OFF"
                                    disabled={!notificationsEnabled}
                                />
                            </div>
                            <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                                Actualizare automată: 30s
                            </div>
                        </div>
                        : null}
                    <CalendarLegend />
                </div>
            ) : viewMode === 'print' ? (
                <div className="print-view">
                    <PrintViewNavigation />
                    <div className="print-content">
                        <div className="print-header" style={{ marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
                            <h2 style={{ margin: 0 }}>Rezervări - {printView === 'day' ? printDate.format('DD MMMM YYYY') :
                                printView === 'week' ? `Săptămâna ${printDate.format('DD MMMM')} - ${moment(printDate).endOf('week').format('DD MMMM YYYY')}` :
                                    printDate.format('MMMM YYYY')}</h2>
                            <p style={{ margin: '5px 0 0 0', color: '#666' }}>Total rezervări: {getFilteredBookingsForPrint().length}</p>
                        </div>

                        <Table
                            dataSource={getFilteredBookingsForPrint()}
                            pagination={false}
                            rowKey="uniqueIdentificationToken"
                            size="small"
                            bordered
                            className="print-table"
                        >
                            <Column title="Data" dataIndex="reservationDate" render={date => moment(date?.split('T')[0]).format('DD.MM.YYYY')} width={100} />
                            <Column title="Ora" render={record => `${record.hourStart?.split(':').slice(0, 2).join(':')} - ${record.hourEnd?.split(':').slice(0, 2).join(':')}`} width={120} />
                            <Column title="Client" dataIndex="customerName" width={150} />
                            <Column title="Telefon" dataIndex="customerPhone" width={120} />
                            <Column title="Persoane" dataIndex="partySize" width={80} align="center" />
                            <Column title="Status" dataIndex="status" render={status => {
                                const statusMap = { 'confirmed': 'Confirmată', 'pending': 'În așteptare', 'cancelled': 'Anulată' };
                                return statusMap[status] || status;
                            }} width={100} />
                            <Column title="Cerințe speciale" dataIndex="specialRequests" ellipsis />
                        </Table>

                        <div className="print-actions" style={{ marginTop: '20px', textAlign: 'center' }}>
                            <Button type="primary" onClick={() => window.print()} size="large">
                                Printează
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <Table
                    dataSource={getSortedBookings()}
                    pagination={{ pageSize: 10 }}
                    rowKey="uniqueIdentificationToken"
                    scroll={{ x: 240 }}
                    rowClassName={(record) => {
                        return record.status === 'pending' ? 'pending-row' : '';
                    }}
                >
                    <Column title="Client" dataIndex="customerName" />
                    <Column title="Telefon" dataIndex="customerPhone" />
                    <Column title="Persoane" dataIndex="partySize" />
                    <Column
                        title="Data"
                        render={record => {
                            const dateStr = record.reservationDate ?
                                (record.reservationDate.includes('T') ?
                                    record.reservationDate.split('T')[0] :
                                    record.reservationDate) : '';
                            return moment(dateStr).format('DD.MM.YYYY');
                        }}
                    />
                    <Column
                        title="Ora"
                        render={record => {
                            const hourStart = record.hourStart ?
                                (record.hourStart.includes(':') ?
                                    record.hourStart.split(':').slice(0, 2).join(':') :
                                    record.hourStart) : '';
                            const hourEnd = record.hourEnd ?
                                (record.hourEnd.includes(':') ?
                                    record.hourEnd.split(':').slice(0, 2).join(':') :
                                    record.hourEnd) : '';

                            return `${hourStart} - ${hourEnd}`;
                        }}
                    />
                    <Column
                        title="Status"
                        dataIndex="status"
                        render={(status, record) => {
                            let statusClass = '';
                            let text = '';

                            if (status === 'confirmed') {
                                statusClass = 'status-confirmed';
                                text = 'Confirmată';
                            } else if (status === 'pending') {
                                statusClass = 'status-pending';
                                text = 'În așteptare';
                            } else if (status === 'cancelled') {
                                statusClass = 'status-cancelled';
                                if (record.cancelledByClient === true) {
                                    text = 'Anulată de client';
                                } else {
                                    text = 'Anulată';
                                }
                            }

                            return (
                                <span className={`status-badge ${statusClass}`}>
                                    {text}
                                </span>
                            );
                        }}
                    />
                    <Column
                        title="Acțiuni"
                        render={(text, record) => (
                            <Button
                                type="default"
                                className="tableAction"
                                icon={<EditOutlined />}
                                onClick={() => onEditElement(record)}
                            >
                                Editează
                            </Button>
                        )}
                    />
                </Table>
            )}

            <Drawer
                title="Rezervare"
                width={window.innerWidth > 1000 ? 1000 : window.innerWidth}
                onClose={onClose}
                visible={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
                footer={
                    <div className="modalFooter">
                        <div style={{ textAlign: 'left' }}>
                            {actionType !== 'new' ? (
                                <Popconfirm title="Sigur vrei să ștergi?" onConfirm={onDelete}>
                                    <Button size="large" danger style={{ marginRight: 8 }}>
                                        Șterge
                                    </Button>
                                </Popconfirm>
                            ) : null}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Button size="large" onClick={onClose} style={{ marginRight: 8 }}>
                                Închide
                            </Button>
                            <Button size="large" onClick={onSave} type="primary">
                                Salvează
                            </Button>
                        </div>
                    </div>
                }
            >
                <Form layout="vertical" form={form} size="large">
                    {newElement.cancelledByClient ?
                        <Alert
                            message="Anulată de client"
                            description=""
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        : null}
                    <div className="form-section">
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="reservationDate"
                                    label="Data"
                                    rules={[{ required: true, message: 'Selectați data' }]}
                                >
                                    <DatePicker
                                        format="DD-MM-YYYY"
                                        onChange={onDateChange}
                                        style={{ width: '100%' }}
                                        disabledDate={(current) => {
                                            if (current && current < moment().startOf('day')) {
                                                return true;
                                            }

                                            if (bookingSettings?.maxAdvanceDays) {
                                                const maxDate = moment().add(bookingSettings.maxAdvanceDays, 'days');
                                                if (current && current > maxDate) {
                                                    return true;
                                                }
                                            }

                                            if (!bookingSettings || loadingSettings) {
                                                return false;
                                            }

                                            const dayIndex = current.day();
                                            let dayProperty = '';
                                            switch (dayIndex) {
                                                case 0: dayProperty = 'sunday'; break;
                                                case 1: dayProperty = 'monday'; break;
                                                case 2: dayProperty = 'tuesday'; break;
                                                case 3: dayProperty = 'wednesday'; break;
                                                case 4: dayProperty = 'thursday'; break;
                                                case 5: dayProperty = 'friday'; break;
                                                case 6: dayProperty = 'saturday'; break;
                                                default: dayProperty = '';
                                            }
                                            const daySettings = bookingSettings.days ? bookingSettings.days[dayProperty] : null;

                                            if (daySettings) {
                                                if (daySettings.enabled === false ||
                                                    !daySettings.timeSlots ||
                                                    daySettings.timeSlots.length === 0) {
                                                    return true;
                                                }
                                                return false;
                                            }

                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="partySize"
                                    label="Număr persoane"
                                    rules={[{ required: true, message: 'Introduceți numărul de persoane' }]}
                                >
                                    <InputNumber
                                        min={1}
                                        max={(() => {
                                            const hourStart = form.getFieldValue('hourStart');
                                            if (hourStart && typeof hourStart.format === 'function' && availableTimeSlots.length > 0) {
                                                return getMaxPartySize(hourStart.format('HH:mm'));
                                            }
                                            return bookingSettings?.maxPartySize || 20;
                                        })()}
                                        onChange={(value) => onNumberChange(value, 'partySize')}
                                        value={newElement.partySize}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="hourStart"
                                    label="Ora început"
                                    rules={[{ required: true, message: 'Selectați ora de început' }]}
                                >
                                    {newElement.isPastReservation ? (
                                        <div>{newElement.hourStart}</div>
                                    ) : capacityLoading ? (
                                        <div style={{ textAlign: 'center', padding: '10px' }}>
                                            <Spin size="small" /> Încărcare slot-uri disponibile...
                                        </div>
                                    ) : availableTimeSlots.length > 0 ? (
                                        <Select
                                            placeholder="Alegeți ora de început"
                                            onChange={(value) => onTimeChange(moment(value, 'HH:mm'), 'hourStart')}
                                            style={{ width: '100%' }}
                                            value={newElement.hourStart || undefined}
                                        >
                                            {availableTimeSlots.map(slot => (
                                                <Option
                                                    key={slot.time}
                                                    value={slot.time}
                                                    disabled={slot.disabled && slot.time !== newElement.hourStart}
                                                >
                                                    {slot.time} {slot.capacity > 0 ? `(Capacitate: ${slot.capacity})` : '(Complet)'}
                                                </Option>
                                            ))}
                                        </Select>
                                    ) : null}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="hourEnd"
                                    label="Ora sfârșit"
                                    rules={[
                                        { required: true, message: 'Selectați ora de sfârșit' },
                                        { validator: validateEndTime }
                                    ]}
                                >
                                    {newElement.isPastReservation ? (
                                        <div>{newElement.hourEnd}</div>
                                    ) : capacityLoading ? (
                                        <div style={{ textAlign: 'center', padding: '10px' }}>
                                            <Spin size="small" /> Încărcare slot-uri disponibile...
                                        </div>
                                    ) : availableTimeSlots.length > 0 ? (
                                        <Select
                                            placeholder="Alegeți ora de final"
                                            onChange={(value) => onTimeChange(moment(value, 'HH:mm'), 'hourEnd')}
                                            style={{ width: '100%' }}
                                            value={newElement.hourEnd || undefined}
                                        >
                                            {availableTimeSlots.map(slot => (
                                                <Option
                                                    key={slot.time}
                                                    value={slot.time}
                                                    disabled={slot.disabled}
                                                >
                                                    {slot.time} {slot.capacity > 0 ? `(Capacitate: ${slot.capacity})` : '(Complet)'}
                                                </Option>
                                            ))}
                                        </Select>
                                    ) : null}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="status"
                                    label="Status"
                                    rules={[{ required: true, message: 'Selectați statusul' }]}
                                >
                                    <Select
                                        placeholder="Status rezervare"
                                        onChange={(value) => onSelectChange(value, 'status')}
                                        value={newElement.status}
                                    >
                                        <Option value="confirmed">Confirmată</Option>
                                        <Option value="pending">În așteptare</Option>
                                        <Option value="cancelled">Anulată</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                {actionType != 'new' ?
                                    <Form.Item label="Acțiuni rapide">
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {newElement.status !== 'confirmed' && (
                                                <Button
                                                    type="primary"
                                                    size="medium"
                                                    onClick={() => handleQuickStatusChange('confirmed')}
                                                    style={{
                                                        backgroundColor: '#28a745',
                                                        borderColor: '#28a745'
                                                    }}
                                                >
                                                    Confirmă
                                                </Button>
                                            )}

                                            {newElement.status !== 'cancelled' && (
                                                <Button
                                                    type="default"
                                                    size="medium"
                                                    onClick={() => handleQuickStatusChange('cancelled')}
                                                    style={{
                                                        backgroundColor: '#dc3545',
                                                        borderColor: '#dc3545',
                                                        color: 'white'
                                                    }}
                                                >
                                                    Anulează
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Item>
                                    : null}
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="sysReservationTypeName"
                                    label="Tip rezervare"
                                    rules={[{ required: true, message: 'Selectați tipul rezervării' }]}
                                >
                                    <Select
                                        placeholder="Selectați tipul rezervării"
                                        onChange={(value) => onSelectChange(value, 'sysReservationTypeName')}
                                        value={newElement.sysReservationTypeName}
                                    >
                                        <Option value="WalkIn">Walk-In</Option>
                                        <Option value="Form">Formular Online</Option>
                                        <Option value="AI">AI</Option>
                                        <Option value="Phone">Telefon</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="form-section">
                        <h3>Informații client</h3>
                        <Form.Item
                            name="customerName"
                            label="Nume client"
                            rules={[{ required: true, message: 'Introduceți numele clientului' }]}
                        >
                            <Input
                                placeholder="Nume client"
                                onBlur={onChange}
                                name="customerName"
                                value={newElement.customerName}
                            />
                        </Form.Item>
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="customerPhone"
                                    label="Telefon"
                                    rules={[{ required: true, message: 'Introduceți numărul de telefon' }]}
                                >
                                    <Input
                                        placeholder="Telefon"
                                        onBlur={onChange}
                                        name="customerPhone"
                                        value={newElement.customerPhone}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="customerEmail"
                                    label="Email"
                                >
                                    <Input
                                        placeholder="Email"
                                        onBlur={onChange}
                                        name="customerEmail"
                                        value={newElement.customerEmail}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="specialRequests"
                            label="Cerințe speciale"
                        >
                            <TextArea
                                rows={4}
                                placeholder="Cerințe speciale"
                                onBlur={onChange}
                                name="specialRequests"
                                value={newElement.specialRequests}
                            />
                        </Form.Item>
                    </div>
                </Form>

                {capacityLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                                Se verifică disponibilitatea...
                            </div>
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};


export default RestaurantBookings;